/**
 * Criterion Link Decorator
 *
 * Appends all criterionticketing.com links using the outbound Link Decorator provided by the TagManager module.
 * The module self-registers criterionticketing.com and its Extra Params (vwo_q,crit_source,crit_sig) via the
 * `tag-manager::outbound-domains` filter, so no manual admin configuration is required.
 *
 * This script currently forwards the following parameters:
 * - VWO cross-domain UUID
 * - WordFly email identifier (Tessitura Constituent ID + Source ID)
 * - Tessitura Source ID derived from admin mapping of another URL parameter (e.g. utm_campaign)
 */

const { hooks, constants } = window.sitchco;

const PARAM_SOURCE = 'crit_source';
const PARAM_SIG = 'crit_sig';
const PARAM_WORDFLY_ID = 'cluid';

function encodeSource(sourceId, constituentId = undefined) {
    const data = {
        sourceId: parseInt(sourceId),
        constituentId: constituentId ? parseInt(constituentId) : undefined,
    };
    return window.btoa(JSON.stringify(data));
}

function getWordFlyParam(searchParams) {
    if (!searchParams.has(PARAM_WORDFLY_ID)) {
        return;
    }

    const [constituentId, sourceId] = searchParams.get(PARAM_WORDFLY_ID).split('=', 2);
    if (!constituentId || !sourceId) {
        return;
    }
    return { [PARAM_SOURCE]: encodeSource(sourceId, constituentId) };
}

function getTessituraSourceParam(searchParams) {
    // source_id is normalized to a positive int and malformed rows are dropped in PHP
    // (CriterionLinkDecorator::normalizeSourceMapping), so no client-side casting is needed.
    const map = window.sitchco.tessituraSourceMapping;
    if (!Array.isArray(map)) {
        return;
    }

    const match = map.find((row) => searchParams.get(row.parameter_key) === row.parameter_value);
    if (!match?.source_id) {
        return;
    }
    return { [PARAM_SOURCE]: encodeSource(match.source_id) };
}

function serializeParams(params) {
    return Object.keys(params)
        .sort()
        .map((key) => `${key}=${params[key]}`)
        .join('&');
}

async function createSignature(params) {
    // The signing key is sha256(apiKey) inlined into the page. crit_sig only deters casual
    // URL tampering — it is not cryptographic integrity.
    const signingKey = window.sitchco.criterionSigningKey;
    if (!signingKey) {
        return '';
    }

    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
        'raw',
        encoder.encode(signingKey),
        {
            name: 'HMAC',
            hash: 'SHA-256',
        },
        false,
        ['sign']
    );
    const hashBuffer = await crypto.subtle.sign('HMAC', key, encoder.encode(serializeParams(params)));
    return Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

async function decorate(produce) {
    // TagManager (a declared dependency) registers updateOutboundDecorator at REGISTER, so it is
    // present by the time this runs at READY — hence no absence guard. Any unexpected failure is
    // logged rather than left to bubble out of the READY handler and break the page.
    try {
        const params = await produce();
        if (!params || Object.keys(params).length === 0) {
            return;
        }

        window.sitchco.tagManager.updateOutboundDecorator(params);
    } catch (error) {
        console.error('Criterion Link Decorator failed:', error);
    }
}

async function decorateSource() {
    return decorate(async () => {
        const searchParams = new URLSearchParams(window.location.search);
        // WordFly always takes precedence over a Tessitura mapping: both produce crit_source and
        // getWordFlyParam is spread last so it wins (it also carries the richer constituentId).
        const sourceParams = {
            ...getTessituraSourceParam(searchParams),
            ...getWordFlyParam(searchParams),
        };
        if (Object.keys(sourceParams).length === 0) {
            return;
        }

        const signature = await createSignature(sourceParams);
        if (!signature) {
            // No signing key configured: emit nothing rather than an unsigned crit_source that
            // Criterion would reject anyway (fail closed).
            return;
        }
        return {
            ...sourceParams,
            [PARAM_SIG]: signature,
        };
    });
}

function decorateVWO() {
    return decorate(() => {
        const crossDomainInfo = window.VWO?.getCrossDomainInfo?.();
        if (!crossDomainInfo?.q || crossDomainInfo.d == null) {
            return;
        }
        return { [crossDomainInfo.q]: crossDomainInfo.d };
    });
}

hooks.addAction(
    constants.READY,
    async () => {
        await decorateSource();
        window.VWO = window.VWO || [];
        window.VWO.push(['onVWOLoaded', decorateVWO]);
    },
    10,
    'criterion-link-decorator'
);
